import { API_URL, TOKEN_KEY } from '../config';

/** Every failed API call throws this. `code` follows the contract `{error, detail}`; two client-side codes exist. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;

  constructor(status: number, code: string, detail: unknown = null) {
    super(`${code}${typeof detail === 'string' && detail ? `: ${detail}` : ''}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

export const NETWORK_ERROR = 'network_error';
export const BAD_RESPONSE = 'bad_response';

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Turn a non-2xx response into ApiError: contract `{error, detail}`, FastAPI `{detail}` or a non-JSON body. */
export async function toApiError(res: Response): Promise<ApiError> {
  let data: unknown = null;
  try {
    const text = await res.text();
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (typeof o.error === 'string') return new ApiError(res.status, o.error, o.detail ?? null);
    if ('detail' in o) return new ApiError(res.status, res.status === 422 ? 'validation_error' : `http_${res.status}`, o.detail);
  }
  const fallback: Record<number, string> = { 401: 'unauthorized', 403: 'forbidden', 404: 'not_found', 409: 'conflict', 429: 'rate_limited' };
  return new ApiError(res.status, fallback[res.status] ?? `http_${res.status}`, null);
}

// ---------------------------------------------------------------- token
/**
 * The bearer token from POST /auth/login. «Remember me» keeps it in localStorage (survives closing the browser),
 * otherwise in sessionStorage (this tab only). The API also sets an httpOnly cookie, but the cookie is shared
 * with the CRM in the same browser, so the app always identifies itself by its own token.
 */
function readToken(): string | null {
  try {
    // this tab's own sign-in («remember me» off) wins over the remembered one shared by all tabs
    return globalThis.sessionStorage?.getItem(TOKEN_KEY) ?? globalThis.localStorage?.getItem(TOKEN_KEY) ?? null;
  } catch {
    return null;
  }
}

let token: string | null = readToken();

export function setToken(value: string | null, remember = true): void {
  const prev = token;
  token = value;
  try {
    const ls = globalThis.localStorage;
    const ss = globalThis.sessionStorage;
    // Clearing: drop only what belongs to this tab — its per-tab token, or the remembered one when that is the token
    // this tab used. A per-tab sign-out never signs the other tabs out (their remembered token is still valid).
    // Signing in without «remember me» stores the token for this tab only and leaves the remembered one alone.
    if (!value) {
      if (ss?.getItem(TOKEN_KEY) != null) ss.removeItem(TOKEN_KEY);
      else if (prev == null || ls?.getItem(TOKEN_KEY) === prev) ls?.removeItem(TOKEN_KEY);
    } else if (remember) {
      ss?.removeItem(TOKEN_KEY);
      ls?.setItem(TOKEN_KEY, value);
    } else {
      ss?.setItem(TOKEN_KEY, value);
    }
  } catch {
    /* storage blocked: the token lives in memory until reload */
  }
}

export function getToken(): string | null {
  return token;
}

/** Re-read the token from storage (tests, another tab logged in). */
export function reloadToken(): void {
  token = readToken();
}

// Another tab of the app logged in / out: follow it, so this tab never keeps using a token that tab replaced.
try {
  globalThis.addEventListener?.('storage', (e: StorageEvent) => {
    if (e.key === TOKEN_KEY || e.key === null) reloadToken();
  });
} catch {
  /* no window (tests) */
}

// ---------------------------------------------------------------- requests
export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  /** Do not call onUnauthorized on 401 (login itself: 401 there means a wrong password or «enter the code»). */
  skipAuthRedirect?: boolean;
  /** Send without the bearer token (public endpoints). */
  anonymous?: boolean;
}

let onUnauthorized: (() => void) | null = null;
let baseUrl = API_URL;

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/** Tests only. */
export function setBaseUrl(url: string): void {
  baseUrl = url.replace(/\/+$/, '');
}

export function buildUrl(path: string, query?: Query): string {
  const url = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    p.append(k, typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v));
  }
  const qs = p.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(path: string, ro: RequestOptions = {}, retried = false): Promise<T> {
  const used = ro.anonymous ? null : token;
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (ro.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(ro.body);
  }
  if (token && !ro.anonymous) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(buildUrl(path, ro.query), {
      method: ro.method ?? (ro.body !== undefined ? 'POST' : 'GET'),
      headers,
      body,
      // never the browser cookie: it may belong to the CRM (another app on the same host) — only our own token
      credentials: 'omit',
      signal: ro.signal,
    });
  } catch (e) {
    if ((e as { name?: string })?.name === 'AbortError') throw e;
    throw new ApiError(0, NETWORK_ERROR, e instanceof Error ? e.message : null);
  }
  if (!res.ok) {
    const err = await toApiError(res);
    if (err.status === 401 && !ro.anonymous && !retried) {
      // the token was replaced in another tab (re-login there): retry once with the current one before giving up
      const fresh = readToken();
      if (fresh && fresh !== used) {
        token = fresh;
        return request<T>(path, ro, true);
      }
    }
    if (err.status === 401 && !ro.skipAuthRedirect) onUnauthorized?.();
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, BAD_RESPONSE, null);
  }
}

export const api = {
  get: <T>(path: string, query?: Query, ro?: Omit<RequestOptions, 'method' | 'body' | 'query'>) =>
    request<T>(path, { ...ro, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, ro?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...ro, method: 'POST', body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: body ?? {} }),
};

/** PUT a file to a presigned storage URL (no API headers, no cookies). Reports progress when XHR is available. */
export function putFile(url: string, file: Blob, contentType: string, onProgress?: (fraction: number) => void): Promise<void> {
  if (typeof XMLHttpRequest === 'undefined' || !onProgress) {
    return fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } }).then(
      (r) => {
        if (!r.ok) throw new ApiError(r.status, 'upload_failed', null);
      },
      (e: unknown) => {
        throw new ApiError(0, NETWORK_ERROR, e instanceof Error ? e.message : null);
      },
    );
  }
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('PUT', url);
    x.setRequestHeader('Content-Type', contentType);
    x.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onProgress(ev.loaded / ev.total);
    };
    x.onload = () => (x.status >= 200 && x.status < 300 ? resolve() : reject(new ApiError(x.status, 'upload_failed', null)));
    x.onerror = () => reject(new ApiError(0, NETWORK_ERROR, null));
    x.send(file);
  });
}
